import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://lgconcretepumping.com';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/schedule#webpage`,
  url: `${SITE_URL}/schedule`,
  name: 'Schedule a Concrete Pumping Job | LG Concrete Pumping Otto NC',
  description:
    'Book a concrete pumping job with LG Concrete Pumping & Stone LLC in Otto, NC. Submit your project details and we\'ll confirm within 24 hours.',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  provider: { '@id': `${SITE_URL}/#business` },
};

const SchedulePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    jobAddress: '',
    preferredDate: '',
    preferredTime: '',
    estimatedYardage: '',
    estimatedHoseLength: '',
    concreteProjectType: '',
    additionalNotes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    document.title = 'Schedule a Concrete Pumping Job | LG Concrete Pumping Otto NC';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Book a concrete pumping job with LG Concrete Pumping & Stone LLC in Otto, NC. Submit your project details and we\'ll confirm within 24 hours.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}/schedule`);

    return () => { script.remove(); };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const params = new URLSearchParams();
    Object.entries(formData).forEach(([key, value]) => params.append(key, value));

    try {
      const res = await fetch('https://readdy.ai/api/form/d6hc8o5pk1ctp7d9b1o0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again or call us directly.');
      }
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative pt-20">
        <div className="relative h-64 overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/985304cb1b49708d8483472612425206.jpg"
            alt="Concrete Pumping Schedule"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="inline-flex items-center gap-2 bg-yellow-500 text-black text-xs font-bold px-4 py-1.5 rounded-full mb-4 uppercase tracking-widest">
              <i className="ri-calendar-check-line"></i>
              Concrete Pumping Only
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3">
              Schedule a <span className="text-yellow-400">Pumping Job</span>
            </h1>
            <p className="text-gray-200 text-lg max-w-2xl">
              Fill out the form below and we'll get back to you within 24 hours to confirm your booking.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-zinc-900 py-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {[
              { icon: 'ri-file-list-3-line', step: '01', title: 'Submit a Request', desc: 'Fill out the booking form with your project details and preferred date.' },
              { icon: 'ri-phone-line', step: '02', title: 'We Confirm', desc: "We'll call or email you within 24 hours to confirm your booking." },
              { icon: 'ri-truck-line', step: '03', title: 'We Show Up', desc: 'Our crew arrives on time, ready to pump on your scheduled day.' },
            ].map((item) => (
              <div key={item.step} className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 flex items-center justify-center bg-yellow-500 rounded-full text-black text-2xl">
                  <i className={item.icon}></i>
                </div>
                <div className="text-yellow-500 text-xs font-bold tracking-widest">STEP {item.step}</div>
                <div className="text-white font-bold text-base">{item.title}</div>
                <div className="text-gray-400 text-sm leading-relaxed">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-extrabold text-black mb-2">Request a Booking</h2>
            <p className="text-gray-500 text-sm">
              Concrete pumping requests only. For stone services, visit our{' '}
              <a href="/services" className="text-yellow-600 underline">Services page</a>.
            </p>
          </div>

          {submitted ? (
            <div className="bg-green-50 border border-green-300 rounded-xl p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-green-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-green-800 mb-2">Request Received!</h3>
              <p className="text-green-700 text-sm">
                Thank you! We'll review your request and get back to you within 24 hours to confirm your concrete pumping job.
              </p>
              <a
                href="tel:8286341304"
                className="inline-flex items-center gap-2 mt-6 bg-yellow-500 text-black px-6 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill"></i>
                Call Us Directly
              </a>
            </div>
          ) : (
            <form
              data-readdy-form
              id="concrete-pumping-booking"
              onSubmit={handleSubmit}
              className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 space-y-5"
            >
              {/* Contact Info */}
              <div className="pb-2 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Contact Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 000-0000"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                />
              </div>

              {/* Job Details */}
              <div className="pb-2 border-b border-gray-100 pt-2">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Job Details</h3>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Job Address *</label>
                <input
                  type="text"
                  name="jobAddress"
                  required
                  value={formData.jobAddress}
                  onChange={handleChange}
                  placeholder="123 Main St, Bryson City, NC 28713"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Preferred Date *</label>
                  <input
                    type="date"
                    name="preferredDate"
                    required
                    value={formData.preferredDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Preferred Start Time *</label>
                  <select
                    name="preferredTime"
                    required
                    value={formData.preferredTime}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 cursor-pointer"
                  >
                    <option value="">Select a time...</option>
                    <option value="6:00 AM">6:00 AM</option>
                    <option value="7:00 AM">7:00 AM</option>
                    <option value="8:00 AM">8:00 AM</option>
                    <option value="9:00 AM">9:00 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="1:00 PM">1:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                  </select>
                </div>
              </div>

              {/* Project Specs */}
              <div className="pb-2 border-b border-gray-100 pt-2">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Project Specifications</h3>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type of Concrete Project *</label>
                <select
                  name="concreteProjectType"
                  required
                  value={formData.concreteProjectType}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 cursor-pointer"
                >
                  <option value="">Select project type...</option>
                  <option value="Poured Wall">Poured Wall</option>
                  <option value="Block Fill">Block Fill</option>
                  <option value="Flatwork (Slab / Driveway / Patio)">Flatwork (Slab / Driveway / Patio)</option>
                  <option value="Footers / Footings">Footers / Footings</option>
                  <option value="Foundation Pour">Foundation Pour</option>
                  <option value="Pool / Gunite">Pool / Gunite</option>
                  <option value="Columns / Piers">Columns / Piers</option>
                  <option value="Retaining Wall">Retaining Wall</option>
                  <option value="Commercial Structural Pour">Commercial Structural Pour</option>
                  <option value="Other">Other (describe below)</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Estimated Yardage (cu. yd.) *
                  </label>
                  <input
                    type="number"
                    name="estimatedYardage"
                    required
                    min="1"
                    value={formData.estimatedYardage}
                    onChange={handleChange}
                    placeholder="e.g. 25"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Estimated Hose Length (ft.) *
                  </label>
                  <input
                    type="number"
                    name="estimatedHoseLength"
                    required
                    min="1"
                    value={formData.estimatedHoseLength}
                    onChange={handleChange}
                    placeholder="e.g. 150"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Additional Notes</label>
                <textarea
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  placeholder="Access details, site conditions, special requirements, etc."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 resize-none"
                ></textarea>
                <div className="text-right text-xs text-gray-400 mt-1">{formData.additionalNotes.length}/500</div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-yellow-500 text-black py-3.5 rounded-lg font-bold text-base hover:bg-yellow-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line"></i>
                    Submit Booking Request
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center">
                By submitting, you agree to be contacted by LG Concrete Pumping &amp; Stone LLC regarding your request.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-yellow-500 py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-extrabold text-black mb-2">Need to Talk to Us Directly?</h3>
          <p className="text-black/70 text-sm mb-6">
            Call us anytime — we're available for urgent pumping jobs and same-week scheduling.
          </p>
          <a
            href="tel:8286341304"
            className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-zinc-800 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-fill text-yellow-400"></i>
            (828) 634-1304
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SchedulePage;
